import React, { useState, useEffect } from 'react';
import { bool, func, object, oneOf, shape } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter, Redirect } from 'react-router-dom';
import Cookies from 'js-cookie';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import authImage from '../../assets/login.png';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { camelize } from '../../util/string';
import { pathByRouteName } from '../../util/routes';
import { apiBaseUrl } from '../../util/api';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { ensureCurrentUser } from '../../util/data';
import {
  isSignupEmailTakenError,
  isTooManyEmailVerificationRequestsError,
} from '../../util/errors';

import { login, authenticationInProgress, signup, signupWithIdp } from '../../ducks/auth.duck';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';
import { sendVerificationEmail } from '../../ducks/user.duck';

import {
  Page,
  Heading,
  NamedRedirect,
  NamedLink,
  LinkTabNavHorizontal,
  SocialLoginButton,
  ResponsiveBackgroundImageContainer,
  Modal,
  LayoutSingleColumn,
} from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';

import TermsAndConditions from './TermsAndConditions/TermsAndConditions';
import ConfirmSignupForm from './ConfirmSignupForm/ConfirmSignupForm';
import LoginForm from './LoginForm/LoginForm';
import SignupForm from './SignupForm/SignupForm';
import EmailVerificationInfo from './EmailVerificationInfo';

// We need to get ToS asset and get it rendered for the modal on this page.
import { TermsOfServiceContent } from '../../containers/TermsOfServicePage/TermsOfServicePage';

// We need to get PrivacyPolicy asset and get it rendered for the modal on this page.
import { PrivacyPolicyContent } from '../../containers/PrivacyPolicyPage/PrivacyPolicyPage';

import { TOS_ASSET_NAME, PRIVACY_POLICY_ASSET_NAME } from './AuthenticationPage.duck';

import css from './AuthenticationPage.module.css';
import { FacebookLogo, GoogleLogo } from './socialLoginLogos';
import signupPhotographer from '../../assets/signup-photographer.png';
import loginPhotographer from '../../assets/login-screen.png';

// Social login buttons are needed by AuthenticationForms
export const SocialLoginButtonsMaybe = props => {
  const routeConfiguration = useRouteConfiguration();
  const { isLogin, showFacebookLogin, showGoogleLogin, from } = props;
  const showSocialLogins = showFacebookLogin || showGoogleLogin;

  const getDefaultRoutes = () => {
    const baseUrl = apiBaseUrl();

    // Route where the user should be returned after authentication
    // This is used e.g. with EditListingPage and ListingPage
    const fromParam = from ? `from=${from}` : '';

    // Default route where user is returned after successfull authentication
    const defaultReturn = pathByRouteName('LandingPage', routeConfiguration);
    const defaultReturnParam = defaultReturn ? `&defaultReturn=${defaultReturn}` : '';

    // Route for confirming user data before creating a new user
    const defaultConfirm = pathByRouteName('ConfirmPage', routeConfiguration);
    const defaultConfirmParam = defaultConfirm ? `&defaultConfirm=${defaultConfirm}` : '';

    return { baseUrl, fromParam, defaultReturnParam, defaultConfirmParam };
  };

  const authWithFacebook = () => {
    const defaultRoutes = getDefaultRoutes();
    const { baseUrl, fromParam, defaultReturnParam, defaultConfirmParam } = defaultRoutes;
    window.location.href = `${baseUrl}/api/auth/facebook?${fromParam}${defaultReturnParam}${defaultConfirmParam}`;
  };

  const authWithGoogle = () => {
    const defaultRoutes = getDefaultRoutes();
    const { baseUrl, fromParam, defaultReturnParam, defaultConfirmParam } = defaultRoutes;
    window.location.href = `${baseUrl}/api/auth/google?${fromParam}${defaultReturnParam}${defaultConfirmParam}`;
  };

  return showSocialLogins ? (
    <div className={css.idpButtons}>
      <div className={css.socialButtonsOr}>
        <span className={css.socialButtonsOrText}>
          <FormattedMessage id="AuthenticationPage.or" />
        </span>
      </div>

      {showFacebookLogin ? (
        <div className={css.socialButtonWrapper}>
          <SocialLoginButton onClick={() => authWithFacebook()}>
            <span className={css.buttonIcon}>{FacebookLogo}</span>
            {isLogin ? (
              <FormattedMessage id="AuthenticationPage.loginWithFacebook" />
            ) : (
              <FormattedMessage id="AuthenticationPage.signupWithFacebook" />
            )}
          </SocialLoginButton>
        </div>
      ) : null}

      {showGoogleLogin ? (
        <div className={css.socialButtonWrapper}>
          <SocialLoginButton onClick={() => authWithGoogle()}>
            <span className={css.buttonIcon}>{GoogleLogo}</span>
            {isLogin ? (
              <FormattedMessage id="AuthenticationPage.loginWithGoogle" />
            ) : (
              <FormattedMessage id="AuthenticationPage.signupWithGoogle" />
            )}
          </SocialLoginButton>
        </div>
      ) : null}
    </div>
  ) : null;
};

// Tabs for SignupForm and LoginForm
export const AuthenticationForms = props => {
  const {
    isLogin,
    showFacebookLogin,
    showGoogleLogin,
    from,
    submitLogin,
    loginError,
    signupError,
    authInProgress,
    submitSignup,
    termsAndConditions,
    history,
    isInvitedUser,
    invitedUserEmail,
    invitedUserFirstName,
    invitedUserLastName,
    photoGrapherId,
    listingId,
    selectedUserRole,
    onUserRoleChange,
  } = props;

  // Accept the customer-role default from router state (existing invite flow)
  // or from a plain ?userRole=customer query param (the profile-page "accept
  // invitation" flow, which does a full page navigation rather than a
  // client-side history.push, so it can't set history state).
  const isCustomer =
    history?.location?.state?.userRole == "customer" ||
    new URLSearchParams(history?.location?.search).get('userRole') === 'customer';
  const customerEmail = history?.location?.state?.email;
  const firstName = history?.location?.state?.firstName;
  const lastName = history?.location?.state?.lastName;

  const fromState = { state: from ? { from } : null };
  const tabs = [
    {
      text: (
        <Heading as={!isLogin ? 'h1' : 'h2'} rootClassName={css.tab}>
          <FormattedMessage id="AuthenticationPage.signupLinkText" />
        </Heading>
      ),
      selected: !isLogin,
      linkProps: {
        name: 'SignupPage',
        to: fromState,
      },
    },
    {
      text: (
        <Heading as={isLogin ? 'h1' : 'h2'} rootClassName={css.tab}>
          <FormattedMessage id="AuthenticationPage.loginLinkText" />
        </Heading>
      ),
      selected: isLogin,
      linkProps: {
        name: 'LoginPage',
        to: fromState,
      },
    },
  ];

  const handleSubmitSignup = values => {
  // listingId alone (without a pre-known email/first/last name - see isInvitedUser
  // below) covers the "accept invitation" flow from a photographer's public
  // profile: the visitor fills in their own details, then the listing they
  // scanned in for gets claimed via updateListing once they've signed up.
  // isInvitedUser always implies listingId, so this is a strict superset of
  // the previous condition - the email-invitation flow is unaffected.
  const signupValues = listingId ? { ...values, photoGrapherId, listingId, confirmPassword: undefined } : values;
  const { fname, lname, ...rest } = signupValues;
  const params = { firstName: fname.trim(), lastName: lname.trim(), ...rest, userRole: selectedUserRole || rest.userRole };
  submitSignup(params);
};

  const loginErrorMessage = (
    <div className={css.error}>
      <FormattedMessage id="AuthenticationPage.loginFailed" />
    </div>
  );

  const signupErrorMessage = (
    <div className={css.error}>
      {isSignupEmailTakenError(signupError) ? (
        <FormattedMessage id="AuthenticationPage.signupFailedEmailAlreadyTaken" />
      ) : (
        <FormattedMessage id="AuthenticationPage.signupFailed" />
      )}
    </div>
  );

  // eslint-disable-next-line no-confusing-arrow
  const errorMessage = (error, message) => (error ? message : null);
  const loginOrSignupError = isLogin
    ? errorMessage(loginError, loginErrorMessage)
    : errorMessage(signupError, signupErrorMessage);

  return (
    <div className={css.content}>
      <div className={css.authHeading}>
        <div className={css.authHeading}>
          {isLogin ? <FormattedMessage id="AuthenticationPage.loginText" /> : <FormattedMessage id="AuthenticationPage.signupText" />}
        </div>
        <div className={css.authDescription}><FormattedMessage id="AuthenticationPage.tagline" /></div>
      </div>
      {isLogin ? (
        <LoginForm
          loginOrSignupError={loginOrSignupError}
          className={css.loginForm}
          onSubmit={submitLogin}
          inProgress={authInProgress}
          customerEmail={customerEmail}
          // tabs={isInvitedUser ? <p className={css.inviteUser}><FormattedMessage id="AuthenticationPage.invitedUser" values={{ email: invitedUserEmail }} /></p> : <LinkTabNavHorizontal className={css.tabs} tabs={tabs} />}
          initialValues={{
            email: customerEmail ? customerEmail : null
          }}
          invitedUserEmail={invitedUserEmail}
        />
      ) : (
        <SignupForm
          className={css.signupForm}
          loginOrSignupError={loginOrSignupError}
          // tabs={isInvitedUser ? <p className={css.inviteUser}><FormattedMessage id="AuthenticationPage.invitedUser" values={{ email: invitedUserEmail }} /></p> : <LinkTabNavHorizontal className={css.tabs} tabs={tabs} />}
          onSubmit={handleSubmitSignup}
          inProgress={authInProgress}
          customerEmail={customerEmail}
          lastName={lastName}
          firstName={firstName}
          termsAndConditions={termsAndConditions}
          initialValues={{
            userRole: selectedUserRole == "customer" || (isCustomer || isInvitedUser) ? 'customer' : 'photographer',
            email: customerEmail ? customerEmail : isInvitedUser ? invitedUserEmail : null,
            lname: lastName || invitedUserLastName,
            fname: firstName || invitedUserFirstName
          }}
          isCustomer={isCustomer}
          isInvitedUser={isInvitedUser}
          invitedUserEmail={invitedUserEmail}
          invitedUserFirstName={invitedUserFirstName}
          invitedUserLastName={invitedUserLastName}
          onUserRoleChange={onUserRoleChange}
        />
      )}

      {/* A client with an existing account scanned a one-time invitation QR code:
          they log in instead, and the profile page claims the invitation for
          them afterwards (see RequestShootSection in ProfilePage.js) - `from`
          sends them back there once they're logged in. */}
      {!isLogin && listingId && photoGrapherId ? (
        <p style={{ textAlign: 'center', marginTop: 16 }}>
          <FormattedMessage id="AuthenticationPage.alreadyHaveAccount" />{' '}
          <NamedLink
            name="LoginPage"
            to={{ search: `?listingId=${listingId}&photoGrapherId=${photoGrapherId}` }}
          >
            <FormattedMessage id="AuthenticationPage.logInLink" />
          </NamedLink>
        </p>
      ) : null}

      <SocialLoginButtonsMaybe
        isLogin={isLogin}
        showFacebookLogin={showFacebookLogin}
        showGoogleLogin={showGoogleLogin}
        from={from}
      />
    </div>
  );
};

// Form for confirming information from IdP (e.g. Facebook)
// This is shown before new user is created to Marketplace API
const ConfirmIdProviderInfoForm = props => {
  const { authInfo, authInProgress, confirmError, submitSingupWithIdp, termsAndConditions } = props;
  const idp = authInfo ? authInfo.idpId.replace(/^./, str => str.toUpperCase()) : null;

  const handleSubmitConfirm = values => {
    const { idpToken, email, firstName, lastName, idpId } = authInfo;
    const { email: newEmail, firstName: newFirstName, lastName: newLastName, ...rest } = values;

    // Pass email, fistName or lastName to Marketplace API only if user has edited them
    // sand they can't be fetched directly from idp provider (e.g. Facebook)

    const authParams = {
      ...(newEmail !== email && { email: newEmail }),
      ...(newFirstName !== firstName && { firstName: newFirstName }),
      ...(newLastName !== lastName && { lastName: newLastName }),
    };

    // If the confirm form has any additional values, pass them forward as user's protected data
    const protectedData = !isEmpty(rest) ? { ...rest } : null;

    submitSingupWithIdp({
      idpToken,
      idpId,
      ...authParams,
      ...(!!protectedData && { protectedData }),
    });
  };

  const confirmErrorMessage = confirmError ? (
    <div className={css.error}>
      {isSignupEmailTakenError(confirmError) ? (
        <FormattedMessage id="AuthenticationPage.signupFailedEmailAlreadyTaken" />
      ) : (
        <FormattedMessage id="AuthenticationPage.signupFailed" />
      )}
    </div>
  ) : null;

  return (
    <div className={css.content}>
      <Heading as="h1" rootClassName={css.signupWithIdpTitle}>
        <FormattedMessage id="AuthenticationPage.confirmSignupWithIdpTitle" values={{ idp }} />
      </Heading>

      <p className={css.confirmInfoText}>
        <FormattedMessage id="AuthenticationPage.confirmSignupInfoText" />
      </p>
      {confirmErrorMessage}
      <ConfirmSignupForm
        className={css.form}
        onSubmit={handleSubmitConfirm}
        inProgress={authInProgress}
        termsAndConditions={termsAndConditions}
        authInfo={authInfo}
        idp={idp}
      />
    </div>
  );
};

export const AuthenticationOrConfirmInfoForm = props => {
  const {
    tab,
    authInfo,
    from,
    showFacebookLogin,
    showGoogleLogin,
    submitLogin,
    submitSignup,
    submitSingupWithIdp,
    authInProgress,
    loginError,
    signupError,
    confirmError,
    termsAndConditions,
    history,
    isInvitedUser,
    invitedUserEmail,
    invitedUserFirstName,
    invitedUserLastName,
    photoGrapherId,
    listingId,
    selectedUserRole,
    onUserRoleChange,
  } = props;
  const isConfirm = tab === 'confirm';
  const isLogin = tab === 'login';
  return isConfirm ? (
    <ConfirmIdProviderInfoForm
      authInfo={authInfo}
      submitSingupWithIdp={submitSingupWithIdp}
      authInProgress={authInProgress}
      confirmError={confirmError}
      termsAndConditions={termsAndConditions}
    />
  ) : (
    <AuthenticationForms
      isLogin={isLogin}
      showFacebookLogin={showFacebookLogin}
      showGoogleLogin={showGoogleLogin}
      from={from}
      loginError={loginError}
      signupError={signupError}
      submitLogin={submitLogin}
      authInProgress={authInProgress}
      submitSignup={submitSignup}
      termsAndConditions={termsAndConditions}
      history={history}
      isInvitedUser={isInvitedUser}
      invitedUserEmail={invitedUserEmail}
      invitedUserFirstName={invitedUserFirstName}
      invitedUserLastName={invitedUserLastName}
      photoGrapherId={photoGrapherId}
      listingId={listingId}
      selectedUserRole={selectedUserRole}
      onUserRoleChange={onUserRoleChange}
    ></AuthenticationForms>
  );
};

const getAuthInfoFromCookies = () => {
  return Cookies.get('st-authinfo')
    ? JSON.parse(Cookies.get('st-authinfo').replace('j:', ''))
    : null;
};
const getAuthErrorFromCookies = () => {
  return Cookies.get('st-autherror')
    ? JSON.parse(Cookies.get('st-autherror').replace('j:', ''))
    : null;
};

export const AuthenticationPageComponent = props => {
  const [tosModalOpen, setTosModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [authInfo, setAuthInfo] = useState(getAuthInfoFromCookies());
  const [authError, setAuthError] = useState(getAuthErrorFromCookies());
  const [selectedUserRole, setSelectedUserRole] = useState('photographer');
  const config = useConfiguration();

  useEffect(() => {
    // Remove the autherror cookie once the content is saved to state
    // because we don't want to show the error message e.g. after page refresh
    if (authError) {
      Cookies.remove('st-autherror');
    }
  }, []);

  // On mobile, it's better to scroll to top.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [tosModalOpen, privacyModalOpen]);

  const {
    authInProgress,
    currentUser,
    intl,
    isAuthenticated,
    location,
    loginError,
    scrollingDisabled,
    signupError,
    submitLogin,
    submitSignup,
    confirmError,
    submitSingupWithIdp,
    tab,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    onResendVerificationEmail,
    onManageDisableScrolling,
    tosAssetsData,
    tosFetchInProgress,
    tosFetchError,
    history,
    isCustomer
  } = props;
  // //////////////
  const searchParams = new URLSearchParams(location.search);

  const invitedUserEmail = searchParams.get('email');
  const invitedUserFirstName = searchParams.get('firstName');
  const invitedUserLastName = searchParams.get('lastName');
  const photoGrapherId = searchParams.get('photoGrapherId');
  const listingId = searchParams.get('listingId');

  const isInvitedUser = invitedUserEmail && invitedUserFirstName && invitedUserLastName && photoGrapherId && listingId;

  // History API has potentially state tied to this route
  // We have used that state to store previous URL ("from"),
  // so that use can be redirected back to that page after authentication.
  const locationFrom = location.state?.from || null;

  const authinfoFrom = authInfo?.from || null;
  const user = ensureCurrentUser(currentUser);
  const currentUserLoaded = !!user.id;
  const isLogin = tab === 'login';

  // A client with an existing account who scanned a one-time invitation QR
  // code logs in via /login?listingId=..&photoGrapherId=.. (link on the signup
  // page); afterwards they're sent back to the invitation, where the profile
  // page claims it for them. Login tab only - after a signup the invitation is
  // claimed right away and the normal post-signup flow applies.
  const inviteFrom =
    isLogin && listingId && photoGrapherId ? `/u/${photoGrapherId}?listing=${listingId}` : null;
  const from = locationFrom ? locationFrom : authinfoFrom ? authinfoFrom : inviteFrom;

  // We only want to show the email verification dialog in the signup
  // tab if the user isn't being redirected somewhere else
  // (i.e. `from` is present). We must also check the `emailVerified`
  // flag only when the current user is fully loaded.
  const showEmailVerification = !isLogin && currentUserLoaded && !user.attributes.emailVerified;

  // Already authenticated, redirect away from auth page
  if (isAuthenticated && from) {
    return <Redirect to={from} />;
  } else if (isAuthenticated && currentUserLoaded && !showEmailVerification) {
    return <NamedRedirect name="LandingPage" />;
  }

  const resendErrorTranslationId = isTooManyEmailVerificationRequestsError(
    sendVerificationEmailError
  )
    ? 'AuthenticationPage.resendFailedTooManyRequests'
    : 'AuthenticationPage.resendFailed';
  const resendErrorMessage = sendVerificationEmailError ? (
    <p className={css.error}>
      <FormattedMessage id={resendErrorTranslationId} />
    </p>
  ) : null;

  const marketplaceName = config.marketplaceName;
  const schemaTitle = isLogin
    ? intl.formatMessage({ id: 'AuthenticationPage.schemaTitleLogin' }, { marketplaceName })
    : intl.formatMessage({ id: 'AuthenticationPage.schemaTitleSignup' }, { marketplaceName });
  const schemaDescription = isLogin
    ? intl.formatMessage({ id: 'AuthenticationPage.schemaDescriptionLogin' }, { marketplaceName })
    : intl.formatMessage({ id: 'AuthenticationPage.schemaDescriptionSignup' }, { marketplaceName });

  const topbarClasses = classNames({
    [css.hideOnMobile]: showEmailVerification,
  });

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        name: schemaTitle,
        description: schemaDescription,
      }}
    >
      <LayoutSingleColumn
        mainColumnClassName={css.layoutWrapperMain}
        topbar={<TopbarContainer className={topbarClasses} />}
        // footer={<FooterContainer />}
      >
        {/* <ResponsiveBackgroundImageContainer */}
        {/* // className={css.root}
         // childrenWrapperClassName={css.contentContainer}
         // as="section"
         // image={config.branding.brandImage}
         // sizes="100%"
        //   useOverlay
        // > */}
        <div className={css.mainContainer}>
          <div className={css.formSection}>
            {showEmailVerification ? (
              <EmailVerificationInfo
                name={user.attributes.profile.firstName}
                email={<span className={css.email}>{user.attributes.email}</span>}
                userRole={user.attributes.profile?.publicData?.userRole}
                onResendVerificationEmail={onResendVerificationEmail}
                resendErrorMessage={resendErrorMessage}
                sendVerificationEmailInProgress={sendVerificationEmailInProgress}
              />
            ) : (
              <AuthenticationOrConfirmInfoForm
                tab={tab}
                authInfo={authInfo}
                from={from}
                showFacebookLogin={!!process.env.REACT_APP_FACEBOOK_APP_ID}
                showGoogleLogin={!!process.env.REACT_APP_GOOGLE_CLIENT_ID}
                submitLogin={submitLogin}
                submitSignup={submitSignup}
                history={history}
                submitSingupWithIdp={submitSingupWithIdp}
                authInProgress={authInProgress}
                loginError={loginError}
                signupError={signupError}
                confirmError={confirmError}
                isInvitedUser={isInvitedUser}
                invitedUserEmail={invitedUserEmail}
                invitedUserFirstName={invitedUserFirstName}
                invitedUserLastName={invitedUserLastName}
                photoGrapherId={photoGrapherId}
                listingId={listingId}
                selectedUserRole={selectedUserRole}       
                onUserRoleChange={setSelectedUserRole}
                termsAndConditions={
                  <TermsAndConditions
                    onOpenTermsOfService={() => setTosModalOpen(true)}
                    onOpenPrivacyPolicy={() => setPrivacyModalOpen(true)}
                    intl={intl}
                  />
                }
              />
            )}
          </div>
          <div className={css.sliderSection}>
            {isLogin ?
            <div className={css.loginBox}>
              <img src={loginPhotographer} alt='signupPhotographer' className={css.coverDesktop} />
              <div className={css.loginGetPayedPhoto}>
                <FormattedMessage
                  id="AuthenticationPage.taglineHighlight"
                  values={{ highlight: chunks => <span className={css.highLightText}>{chunks}</span> }}
                />
              </div>
            </div>
            :
            <div className={css.signupBox}>
              <img src={signupPhotographer} alt='signupPhotographer' className={css.coverDesktop} />
              {selectedUserRole == "customer" ?
                <div className={css.forPhotographer}>
                  <div className={css.imageHeading}><FormattedMessage id="AuthenticationPage.forCustomers" /></div>
                  <ul>
                    <li><FormattedMessage id="AuthenticationPage.customerPoint1" /></li>
                    <li><FormattedMessage id="AuthenticationPage.customerPoint2" /></li>
                    <li><FormattedMessage id="AuthenticationPage.customerPoint3" /></li>
                  </ul>
                </div>
                :
                <div className={css.forPhotographer}>
                  <div className={css.imageHeading}><FormattedMessage id="AuthenticationPage.forPhotographers" /></div>
                  <ul>
                    <li><FormattedMessage id="AuthenticationPage.photographerPoint1" /></li>
                    <li><FormattedMessage id="AuthenticationPage.photographerPoint2" /></li>
                    <li><FormattedMessage id="AuthenticationPage.photographerPoint3" /></li>
                  </ul>
                </div>
              }
            </div>
            }
          </div>
        </div>
          {/* </ResponsiveBackgroundImageContainer> */}
      </LayoutSingleColumn>
      <Modal
        id="AuthenticationPage.tos"
        isOpen={tosModalOpen}
        onClose={() => setTosModalOpen(false)}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.termsWrapper}>
          <TermsOfServiceContent
            inProgress={tosFetchInProgress}
            error={tosFetchError}
            data={tosAssetsData?.[camelize(TOS_ASSET_NAME)]?.data}
          />
        </div>
      </Modal>
      <Modal
        id="AuthenticationPage.privacyPolicy"
        isOpen={privacyModalOpen}
        onClose={() => setPrivacyModalOpen(false)}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.privacyWrapper}>
          <PrivacyPolicyContent
            inProgress={tosFetchInProgress}
            error={tosFetchError}
            data={tosAssetsData?.[camelize(PRIVACY_POLICY_ASSET_NAME)]?.data}
          />
        </div>
      </Modal>
    </Page>
  );
};

AuthenticationPageComponent.defaultProps = {
  currentUser: null,
  loginError: null,
  signupError: null,
  confirmError: null,
  tab: 'signup',
  sendVerificationEmailError: null,
  showSocialLoginsForTests: false,
  privacyAssetsData: null,
  privacyFetchInProgress: false,
  privacyFetchError: null,
  tosAssetsData: null,
  tosFetchInProgress: false,
  tosFetchError: null,
};

AuthenticationPageComponent.propTypes = {
  authInProgress: bool.isRequired,
  currentUser: propTypes.currentUser,
  isAuthenticated: bool.isRequired,
  loginError: propTypes.error,
  scrollingDisabled: bool.isRequired,
  signupError: propTypes.error,
  confirmError: propTypes.error,

  submitLogin: func.isRequired,
  submitSignup: func.isRequired,
  tab: oneOf(['login', 'signup', 'confirm']),

  sendVerificationEmailInProgress: bool.isRequired,
  sendVerificationEmailError: propTypes.error,
  onResendVerificationEmail: func.isRequired,
  onManageDisableScrolling: func.isRequired,

  // to fetch privacy-policy page asset
  // which is shown in modal
  privacyAssetsData: object,
  privacyFetchInProgress: bool,
  privacyFetchError: propTypes.error,

  // to fetch terms-of-service page asset
  // which is shown in modal
  tosAssetsData: object,
  tosFetchInProgress: bool,
  tosFetchError: propTypes.error,

  // from withRouter
  location: shape({ state: object }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { isAuthenticated, loginError, signupError, confirmError } = state.auth;
  const { currentUser, sendVerificationEmailInProgress, sendVerificationEmailError } = state.user;
  const {
    pageAssetsData: privacyAssetsData,
    inProgress: privacyFetchInProgress,
    error: privacyFetchError,
  } = state.hostedAssets || {};
  const { pageAssetsData: tosAssetsData, inProgress: tosFetchInProgress, error: tosFetchError } =
    state.hostedAssets || {};

  return {
    authInProgress: authenticationInProgress(state),
    currentUser,
    isAuthenticated,
    loginError,
    scrollingDisabled: isScrollingDisabled(state),
    signupError,
    confirmError,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    privacyAssetsData,
    privacyFetchInProgress,
    privacyFetchError,
    tosAssetsData,
    tosFetchInProgress,
    tosFetchError,
  };
};

const mapDispatchToProps = dispatch => ({
  submitLogin: ({ email, password }) => dispatch(login(email, password)),
  submitSignup: params => dispatch(signup(params)),
  submitSingupWithIdp: params => dispatch(signupWithIdp(params)),
  onResendVerificationEmail: () => dispatch(sendVerificationEmail()),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const AuthenticationPage = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  injectIntl
)(AuthenticationPageComponent);

export default AuthenticationPage;
